import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/cliente';
import type { OrdenCompra, OrdenCompraDetalle, Pagina } from '../api/tipos';
import { Cargando, Error, Paginacion } from '../componentes/comunes';
import { Recepcion } from './Compras';

/**
 * La mercancía que está por llegar.
 *
 * Recibir es de almacén, no de compras: quien negoció la compra no debe
 * ser quien confirma que llegó. Es la misma separación que ya rige los
 * pagos —quien autoriza no ejecuta— y la que impide que una compra se dé
 * por recibida sin que nadie haya visto la caja.
 *
 * La regla ya estaba en la API desde el principio: registrar una
 * recepción exige permiso de almacén. Lo que faltaba era la puerta, porque
 * el botón vivía en la pantalla de Compras, donde almacén no entra.
 *
 * La recepción puede ser parcial. Llegan tres de cinco más veces de las
 * que a nadie le gustaría, y obligar a esperar el pedido completo para
 * registrar algo deja tres piezas fuera del inventario y una orden
 * esperándolas.
 */
export default function PorRecibir() {
  const [pagina, setPagina] = useState<Pagina<OrdenCompra> | null>(null);
  const [numero, setNumero] = useState(1);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [recibiendo, setRecibiendo] = useState<OrdenCompraDetalle | null>(null);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    api.get<Pagina<OrdenCompra>>('/ordenes-compra/por-recibir', {
      pagina: numero, limite: 25,
    })
      .then(setPagina)
      .catch(setError)
      .finally(() => setCargando(false));
  }, [numero]);

  useEffect(cargar, [cargar]);

  function abrir(id: string) {
    api.get<OrdenCompraDetalle>(`/ordenes-compra/por-recibir/${id}`)
      .then(setRecibiendo)
      .catch(setError);
  }

  return (
    <div className="panel">
      <Error error={error} />

      {cargando && !pagina ? <Cargando /> : pagina && (
        <>
          {pagina.data.length === 0 ? (
            <div className="vacio">No hay mercancía por recibir.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Orden de compra</th>
                  <th>Proveedor</th>
                  <th>Para la orden</th>
                  <th className="numero">Partidas</th>
                  <th>Entrega estimada</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {pagina.data.map((o) => (
                  <tr key={o.id}>
                    <td className="mono">
                      <button className="enlace" onClick={() => abrir(o.id)}>
                        {o.folio}
                      </button>
                    </td>
                    <td>{o.proveedor?.nombre ?? '—'}</td>
                    <td className="mono">{o.ordenTrabajo?.folio ?? '—'}</td>
                    <td className="numero">{o.numeroPartidas}</td>
                    <td>
                      {o.fechaEntregaEstimada
                        ? new Date(o.fechaEntregaEstimada).toLocaleDateString('es-MX', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        })
                        : '—'}
                    </td>
                    <td>
                      {o.estatus === 'recibida_parcial'
                        ? <span className="etiqueta">Recibida a medias</span>
                        : <span className="etiqueta si">En camino</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <Paginacion
            pagina={pagina.pagina} limite={pagina.limite} total={pagina.total}
            onCambiar={setNumero}
          />
        </>
      )}

      {recibiendo && (
        <Recepcion
          orden={recibiendo}
          onCerrar={() => setRecibiendo(null)}
          onHecho={() => { setRecibiendo(null); cargar(); }}
        />
      )}
    </div>
  );
}
